<template>
  <section class="background-card-group">
    <div
      v-if="hasText"
      class="background-card-group__text-content"
      :class="`background-card-group__text-content--align-${alignContent}`"
    >
      <c-kicker
        v-if="$slots.overline"
        class="background-card-group__overline"
        :align="alignContent"
        :size="overlineSize"
        :color="overlineColor"
      >
        <slot name="overline" />
      </c-kicker>

      <c-title
        v-if="$slots.title"
        class="background-card-group__title"
        :align="alignContent"
        :size="titleSize"
        :color="titleColor"
      >
        <slot name="title" />
      </c-title>

      <c-text
        v-if="$slots.subtitle"
        class="background-card-group__subtitle"
        :align="alignContent"
        :size="subtitleSize"
        :color="subtitleColor"
      >
        <slot name="subtitle" />
      </c-text>

      <div
        v-if="hasButtons && $slots.buttons"
        class="background-card-group__actions"
      >
        <slot name="buttons" />
      </div>
    </div>

    <!-- SWIPER -->
    <div v-if="useSwiper" class="background-card-group__swiper" :class="{ 'background-card-group__swiper--arrows-below': arrowsBelow }">
      <div ref="swiperContainer" class="swiper-container background-card-group__swiper-container">
        <div class="swiper-wrapper">
          <slot />
        </div>
      </div>

      <div v-if="arrowsBelow" class="background-card-group__swiper-controls" :class="`background-card-group__swiper-controls--${arrowsPosition}`">
        <button
          ref="prevBtn"
          class="background-card-group__swiper-btn background-card-group__swiper-btn--prev"
          :style="{ backgroundColor: arrowBackgroundColor }"
          aria-label="Previous"
        >
          <svg><use xlink:href="#chevron-left" /></svg>
        </button>

        <button
          ref="nextBtn"
          class="background-card-group__swiper-btn background-card-group__swiper-btn--next"
          :style="{ backgroundColor: arrowBackgroundColor }"
          aria-label="Next"
        >
          <svg><use xlink:href="#chevron-right" /></svg>
        </button>
      </div>

      <template v-else>
        <button
          ref="prevBtn"
          class="background-card-group__swiper-btn background-card-group__swiper-btn--prev"
          :style="{ backgroundColor: arrowBackgroundColor }"
          aria-label="Previous"
        >
          <svg><use xlink:href="#chevron-left" /></svg>
        </button>

        <button
          ref="nextBtn"
          class="background-card-group__swiper-btn background-card-group__swiper-btn--next"
          :style="{ backgroundColor: arrowBackgroundColor }"
          aria-label="Next"
        >
          <svg><use xlink:href="#chevron-right" /></svg>
        </button>
      </template>
    </div>

    <div v-else class="background-card-group__cards">
      <slot />
    </div>
  </section>
</template>

<script>
import Swiper from 'swiper'
import CKicker from '../../02_atoms/kicker/kicker.vue'
import CTitle from '../../00_v2/title/title.vue'
import CText from '../../00_v2/text/text.vue'

export default {
  name: 'background-card-group',
  props: {
    hasText: {
      type: Boolean,
      default: true,
    },
    hasButtons: {
      type: Boolean,
      default: true,
    },
    alignContent: {
      type: String,
      default: 'center',
      validator: (value) => ['left', 'center', 'right'].includes(value),
    },
    useSwiper: {
      type: Boolean,
      default: true,
    },
    overlineSize: {
      type: String,
      default: 'xs',
      validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value),
    },
    overlineColor: {
      type: String,
      default: '#88D49E',
    },
    titleSize: {
      type: String,
      default: 'xl',
      validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'].includes(value),
    },
    titleColor: {
      type: String,
      default: '#1d2822',
    },
    subtitleSize: {
      type: String,
      default: 'md',
      validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value),
    },
    subtitleColor: {
      type: String,
      default: '#1d2822',
    },
    arrowBackgroundColor: {
      type: String,
      default: '#88D49E',
    },
    arrowsBelow: {
      type: Boolean,
      default: false,
    },
    arrowsPosition: {
      type: String,
      default: 'center',
      validator: (value) => ['left', 'center', 'right'].includes(value),
    },
  },
  data() {
    return {
      swiper: null,
    }
  },
  components: {
    CTitle,
    CText,
    CKicker,
  },
  mounted() {
    if (this.useSwiper) {
      this.$nextTick(() => this.initSwiper())
    }
  },
  beforeDestroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true)
      this.swiper = null
    }
  },
  methods: {
    initSwiper() {
      const wrapper = this.$refs.swiperContainer.querySelector('.swiper-wrapper')
      Array.from(wrapper.children).forEach((child) => {
        child.classList.add('swiper-slide')
      })

      this.swiper = new Swiper(this.$refs.swiperContainer, {
        slidesPerView: 1,
        spaceBetween: 16,
        breakpointsInverse: true,
        breakpoints: {
          768: {
            slidesPerView: 2,
          },
          1024: {
            slidesPerView: 3,
          },
          1440: {
            slidesPerView: 4,
          },
        },
        navigation: {
          nextEl: this.$refs.nextBtn,
          prevEl: this.$refs.prevBtn,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.background-card-group {
  &__text-content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.5rem;

    &--align-left {
      align-items: flex-start;
      text-align: left;
    }

    &--align-center {
      align-items: center;
      text-align: center;
    }

    &--align-right {
      align-items: flex-end;
      text-align: right;
    }
  }

  &__overline {
    font-weight: 700;
    letter-spacing: 0.0625rem;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;

     @media screen and (min-width: 768px) {
      flex-direction: row;
      width: auto;
     }
  }

  &__cards {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    @media screen and (min-width: 768px) {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(19.25rem, 1fr));
    }
  }

  &__swiper {
    position: relative;
    overflow: hidden;

    &--arrows-below {
      overflow: visible;
    }
  }

  &__swiper-controls {
    display: flex;
    gap: 1rem;
    margin-top: 1.5rem;

    &--left {
      justify-content: flex-start;
    }

    &--center {
      justify-content: center;
    }

    &--right {
      justify-content: flex-end;
    }

    .background-card-group__swiper-btn {
      position: static;
      width: 3rem;
      height: 3rem;
      border-radius: 50%;
      transition: all 0.8s ease-in-out;

      & > svg {
        width: 1rem;
        height: 1rem;
      }

      &:hover {
        background: #88D49E80 !important;

        & > svg {
          fill: #fff;
        }
      }

      &::before {
        display: none;
      }
    }
  }

  &__swiper-container {
    overflow: hidden;
    padding: 0 !important;

    .swiper-slide {
      box-sizing: border-box;
      flex-shrink: 0;
      width: 100%;
    }
  }

  &__swiper-btn {
    position: absolute;
    top: 40%;
    z-index: 20;
    display: flex;
    align-items: center;
    align-content: center;
    text-align: center;
    justify-content: center;
    width: 6.5rem;
    height: 5rem;
    border: none;
    background-image: none;
    cursor: pointer;

    &:focus {
      outline: none !important;
    }

    svg {
      display: block;
      fill: #1d2822;
      color: #1d2822;
      width: 2.0625rem;
      height: 2.0625rem;
    }

    &--prev {
      border-radius: 0.125rem 6.25rem 6.25rem 0.125rem;
      left: 0;
    }

    &--next {
      border-radius: 6.25rem 0.125rem 0.125rem 6.25rem;
      right: 0;
    }

    &.swiper-button-disabled {
      opacity: 0.35;
      cursor: default;
      pointer-events: none;
    }

    @media screen and (max-width: 767px) {
      width: 3.4375rem;
      height: 5rem;
      top: 35%;

      svg {
        width: 1.4375rem;
        height: 1.4375rem;
      }
    }

    @media screen and (min-width: 768px) {
      transition-duration: 200ms;
      animation-timing-function: ease-in;

      &::before {
        position: absolute;
        content: "";
        width: 9.375rem;
        height: 37.5rem;
      }

      &--prev {
        left: -4.0625rem;
        &:hover {
          left: 0;
        }
      }

      &--next {
        right: -4.0625rem;
        &:hover {
          right: 0;
        }
      }
    }
  }
}

.swiper-wrapper {
  padding: 0.0625rem;
}
</style>
